/**
 * ProgramSchedulePanel — Communications ▸ Program Schedule (AJ, 2026-10-12).
 * Replaces the "SWS Program Schedule" Google Sheet + "Activity Reminder" script.
 * One recurring task list per sport (sports from WP Mgmt ▸ Sports), emailed
 * monthly to an AD-configured staff group (e.g. Steering Committee). The email
 * carries a tokenized link (/program-schedule/:token) that lets any recipient
 * add/edit/delete tasks. Condensed, inline-edit, saves on change.
 * Backend: routes/comm.py. Doc: docs/PROGRAM_SCHEDULE.md.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Send, Link2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/comm`;
const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const inp = 'h-7 text-xs rounded border border-slate-300 px-2 bg-white text-slate-900';
// Condensed grid cell: looks like plain text, shows an edit box on hover/focus.
const cellInp = 'h-6 w-full text-xs px-1 rounded bg-transparent border border-transparent hover:border-slate-300 focus:border-teal-400 focus:bg-white focus:outline-none text-slate-900';
const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'}`;

/** Inline-edit task table. `api` = { list, create, update, remove } so the same
 *  table serves the logged-in tab and the public tokenized page. */
export function TaskTable({ api }) {
  const [rows, setRows] = useState(null);
  const [draft, setDraft] = useState({ month: new Date().getMonth() + 1, day: '', title: '', assignee: '', details: '' });

  const load = useCallback(async () => {
    try { setRows(await api.list()); } catch (e) { toast.error(e.response?.data?.detail || 'Could not load tasks'); setRows([]); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const patch = async (row, field, value) => {
    const v = field === 'day' ? (value === '' ? null : Number(value)) : field === 'month' ? Number(value) : value;
    if (row[field] === v) return;
    setRows((rs) => rs.map((r) => (r.id === row.id ? { ...r, [field]: v } : r)));
    try { await api.update(row.id, { [field]: v }); } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); load(); }
  };
  const add = async () => {
    if (!draft.title.trim()) { toast.error('Enter an activity'); return; }
    try {
      await api.create({ ...draft, day: draft.day === '' ? null : Number(draft.day) });
      setDraft((d) => ({ ...d, day: '', title: '', assignee: '', details: '' })); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };
  const remove = async (row) => {
    try { await api.remove(row.id); setRows((rs) => rs.filter((r) => r.id !== row.id)); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (!rows) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  const sorted = [...rows].sort((a, b) => (a.month - b.month) || ((a.day || 0) - (b.day || 0)) || ((a.position || 0) - (b.position || 0)));
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="ps-task-table">
      <table className="w-full text-xs">
        <thead className="bg-slate-50"><tr className="text-left text-[10px] uppercase text-slate-400">
          <th className="px-2 py-1 w-24">Month</th><th className="w-12">Day</th><th>Activity</th><th className="w-40">Who</th><th>Notes</th><th className="w-6" /></tr></thead>
        <tbody>
          {sorted.map((r, i) => {
            const first = i === 0 || sorted[i - 1].month !== r.month;
            return (
            <tr key={r.id} className={`group ${first && i > 0 ? 'border-t border-slate-300' : ''} hover:bg-slate-50`}>
              <td className="px-1"><select className={`${cellInp} appearance-none ${first ? 'font-semibold' : 'text-transparent group-hover:text-slate-400 focus:text-slate-900'}`} value={r.month} onChange={(e) => patch(r, 'month', e.target.value)}>
                {MONTHS.slice(1).map((m, k) => <option key={m} value={k + 1} className="text-slate-900">{m}</option>)}</select></td>
              <td><input className={`${cellInp} text-red-600`} type="number" min="1" max="31" defaultValue={r.day ?? ''} onBlur={(e) => patch(r, 'day', e.target.value)} /></td>
              <td><input className={cellInp} defaultValue={r.title} onBlur={(e) => patch(r, 'title', e.target.value.trim())} /></td>
              <td><input className={cellInp} defaultValue={r.assignee || ''} onBlur={(e) => patch(r, 'assignee', e.target.value)} /></td>
              <td><input className={`${cellInp} text-slate-500`} defaultValue={r.details || ''} onBlur={(e) => patch(r, 'details', e.target.value)} /></td>
              <td className="text-center"><button className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500" onClick={() => remove(r)} title="Delete"><Trash2 className="h-3 w-3" /></button></td>
            </tr>
            );
          })}
          <tr className="border-t-2 border-slate-200 bg-slate-50">
            <td className="px-2 py-1"><select className={`${inp} w-full`} value={draft.month} onChange={(e) => setDraft({ ...draft, month: Number(e.target.value) })} data-testid="ps-new-month">
              {MONTHS.slice(1).map((m, k) => <option key={m} value={k + 1}>{m}</option>)}</select></td>
            <td><input className={`${inp} w-12`} type="number" min="1" max="31" value={draft.day} onChange={(e) => setDraft({ ...draft, day: e.target.value })} /></td>
            <td><input className={`${inp} w-full`} placeholder="New activity" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && add()} data-testid="ps-new-title" /></td>
            <td><input className={`${inp} w-full`} placeholder="Who" value={draft.assignee} onChange={(e) => setDraft({ ...draft, assignee: e.target.value })} /></td>
            <td><input className={`${inp} w-full`} placeholder="Notes" value={draft.details} onChange={(e) => setDraft({ ...draft, details: e.target.value })} /></td>
            <td className="text-center"><button className="text-teal-600 hover:text-teal-800" onClick={add} title="Add" data-testid="ps-add"><Plus className="h-4 w-4" /></button></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function ProgramSchedulePanel({ orgId }) {
  const [ov, setOv] = useState(null);
  const [sport, setSport] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    try {
      const r = await axios.get(`${API}/program/${orgId}`);
      // Open on the busiest list (most tasks) — today that is Basketball.
      const busiest = [...r.data.sports].sort((a, b) => b.task_count - a.task_count)[0];
      setOv(r.data); setSport((s) => s || busiest?.key || '');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load Program Schedule'); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const cur = ov?.sports.find((s) => s.key === sport);
  const sch = cur?.schedule || {};
  // Default recipients: this sport's own steering group if the AD made one,
  // else the org-wide Steering Committee. Each sport can pick any group.
  const sg = ov?.staff_groups || [];
  const defaultGroup = (sg.find((g) => /steering/i.test(g.name || '') && cur && (g.name || '').toLowerCase().includes(cur.title.toLowerCase()))
    || sg.find((g) => /steering/i.test(g.name || '')))?.id || '';

  const saveSchedule = async (patch) => {
    try {
      await axios.put(`${API}/schedule/${orgId}/${sport}`, {
        staff_group_id: sch.staff_group_id || defaultGroup || null, ...patch });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const sendNow = async () => {
    setSending(true);
    try { const r = await axios.post(`${API}/send-now/${orgId}/${sport}`); toast.success(`Sent to ${r.data.data.delivered_count} of ${r.data.data.recipient_count}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); } finally { setSending(false); }
  };
  const copyLink = () => { navigator.clipboard?.writeText(cur.edit_url); toast.success('Edit link copied'); };
  const resetLink = async () => {
    if (!window.confirm('Issue a new edit link? The link in past emails will stop working.')) return;
    try { await axios.post(`${API}/program/${orgId}/${sport}/reset-link`); toast.success('New link issued'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Reset failed'); }
  };

  const api = React.useMemo(() => ({
    list: async () => (await axios.get(`${API}/tasks/${orgId}/${sport}`)).data.data,
    create: (b) => axios.post(`${API}/tasks/${orgId}/${sport}`, b),
    update: (id, b) => axios.patch(`${API}/tasks/${orgId}/${sport}/${id}`, b),
    remove: (id) => axios.delete(`${API}/tasks/${orgId}/${sport}/${id}`),
  }), [orgId, sport]);

  if (!ov) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="space-y-3 text-sm" data-testid="program-schedule">
      <div className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1">
        {ov.sports.map((s) => (
          <Button key={s.key} variant="ghost" size="sm" onClick={() => setSport(s.key)}
            className={`h-7 px-3 text-xs border ${sport === s.key ? 'bg-teal-50 text-teal-700 border-teal-200' : 'border-transparent text-slate-500'}`}
            data-testid={`ps-sport-${s.key}`}>
            <span className={`mr-1.5 h-2 w-2 rounded-full ${s.schedule?.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />{s.title} <span className="ml-1 text-slate-400">{s.task_count}</span>
          </Button>
        ))}
      </div>

      {cur && (
        <>
          <div className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 ${sch.enabled ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
            <Switch checked={!!sch.enabled} onCheckedChange={(v) => saveSchedule({ enabled: v })} data-testid="ps-enabled" />
            <span className="font-semibold text-slate-800">{sch.enabled ? `${cur.title}: monthly email ON` : `${cur.title}: monthly email OFF`}</span>
            <label className="flex items-center gap-1 text-xs text-slate-600">Day
              <select className={inp} value={sch.send_day_of_month || 1} onChange={(e) => saveSchedule({ send_day_of_month: Number(e.target.value) })} data-testid="ps-day">
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}</select></label>
            <label className="flex items-center gap-1 text-xs text-slate-600">at
              <select className={inp} value={sch.send_hour ?? 6} onChange={(e) => saveSchedule({ send_hour: Number(e.target.value) })}>
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}</select></label>
            <label className="flex items-center gap-1 text-xs text-slate-600">To
              <select className={inp} value={sch.staff_group_id || (sch.group_id ? '' : defaultGroup)} onChange={(e) => saveSchedule({ staff_group_id: e.target.value })} data-testid="ps-group">
                <option value="">{sch.group_id && !sch.staff_group_id ? '— old list (pick a group) —' : '— pick a group —'}</option>
                {ov.staff_groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>
              <span className="text-slate-400">({cur.recipient_count})</span></label>
            <label className="flex items-center gap-1 text-xs text-slate-600">+
              <input className={`${inp} w-56`} placeholder="extra emails, comma separated" defaultValue={(sch.extra_recipients || []).join(', ')}
                key={`${sport}-${(sch.extra_recipients || []).join(',')}`}
                onBlur={(e) => saveSchedule({ extra_recipients: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} data-testid="ps-extra" /></label>
            <div className="ml-auto flex items-center gap-1">
              {cur.edit_url && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={copyLink} title={cur.edit_url}><Link2 className="h-3 w-3 mr-1" />Edit link</Button>}
              {cur.edit_url && <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={resetLink} title="Issue a new edit link"><RefreshCw className="h-3 w-3" /></Button>}
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={sending || !sch.org_id} onClick={sendNow} data-testid="ps-send-now"><Send className="h-3 w-3 mr-1" />Send now</Button>
            </div>
          </div>
          {sch.last_sent_at && <p className="text-[11px] text-slate-500">Last sent {new Date(sch.last_sent_at).toLocaleString()} · next {sch.next_scheduled_at ? new Date(sch.next_scheduled_at).toLocaleString() : '—'}</p>}
          <TaskTable key={sport} api={api} />
        </>
      )}
    </div>
  );
}
